import { Award, Eye } from 'lucide-react';
import type { TabWhatGoodLooksLike, DifficultyLevel } from '../../../../types/craft';
import FormattedText from '../../../common/FormattedText';

interface Props {
  data: TabWhatGoodLooksLike;
}

const LEVEL_COLORS: Record<string, { header: string; cell: string }> = {
  poor: { header: 'bg-app-critical/10 text-app-critical', cell: 'text-app-critical' },
  average: { header: 'bg-app-warning/10 text-app-warning', cell: 'text-app-warning' },
  excellent: { header: 'bg-app-positive/10 text-app-positive', cell: 'text-app-positive' },
};

export default function WhatGoodLooksLikeTab({ data }: Props) {
  if (!data?.summary && !data?.standards?.length) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-muted">No data available yet.</p>
        <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {data.summary && (
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-app-positive" />
            <h4 className="text-sm font-semibold text-app-ink">Excellence Summary</h4>
          </div>
          <FormattedText text={data.summary} className="text-sm text-app-ink" />
        </div>
      )}

      {data.standards?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-4">Excellence Standards</h4>
          <div className="space-y-4">
            {data.standards.map((std, i) => (
              <div key={i} className="bg-app-surface border border-app-line rounded-lg overflow-hidden">
                <div className="px-5 py-3 bg-app-canvas border-b border-app-line">
                  <h5 className="text-sm font-semibold text-app-ink">{std.dimension}</h5>
                </div>
                <div className="grid grid-cols-1 divide-y divide-app-line sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                  {(['poor', 'average', 'excellent'] as const).map(level => (
                    <div key={level} className="p-4">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider mb-2 ${LEVEL_COLORS[level].header}`}>
                        {level}
                      </span>
                      <p className="text-xs text-app-muted leading-relaxed">{std[level]}</p>
                    </div>
                  ))}
                </div>
                {std.observable_behaviours?.length > 0 && (
                  <div className="px-5 py-3 bg-app-focus/10 border-t border-app-focus/30">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Eye className="w-3 h-3 text-app-focus" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-app-focus">Observable Behaviours</span>
                    </div>
                    <ul className="space-y-1">
                      {std.observable_behaviours.map((b, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-xs text-app-focus">
                          <span className="w-1 h-1 rounded-full bg-app-focus mt-1.5 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.exemplar_profile && (
        <div className="bg-app-positive/10 border border-app-positive rounded-lg p-5">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-app-positive" />
            <h4 className="text-sm font-semibold text-app-positive">Exemplar Profile</h4>
          </div>
          <FormattedText text={data.exemplar_profile} className="text-sm text-app-ink" />
        </div>
      )}
    </div>
  );
}
